// apps/spa/src/routes/AuditLog.tsx
// /audit — Audit log viewer (Plan 04-03 replaces the 04-01 placeholder).
//
// Header.tsx and App.tsx are already wired (Plan 04-01) — this file only changes
// what the /audit route renders.
//
// Layout:
//   - Skeleton while initial query loads
//   - Empty state ("No audit entries yet") when entries.length === 0
//   - Table with Time / Officer / Action / Target / ▾ columns
//   - 'Load 50 more' button at the bottom (hidden when hasNextPage is false)
//
// Target resolution: knownTargetIds is built from useReminders + useCommands caches.
// Any targetId NOT in the set renders as strikethrough (entity was deleted).
//
// Phase 7 (Plan 07-05): AnimatePresence + motion.div wraps per-page entry additions.

import { motion, AnimatePresence, useReducedMotion } from 'motion/react';
import { useAuditLog } from '@/hooks/useAuditLog';
import { useReminders } from '@/hooks/useReminders';
import { useCommands } from '@/hooks/useCommands';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TooltipProvider } from '@/components/ui/tooltip';
import { AuditLogRow } from '@/components/audit/AuditLogRow';
import { fadeUp } from '@/lib/motion';

export default function AuditLog() {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useAuditLog();

  const { data: reminders } = useReminders();
  const { data: commands } = useCommands();
  const reduce = useReducedMotion();

  // Build set of known (non-deleted) entity IDs from live query caches.
  const knownTargetIds = new Set<string>([
    ...(reminders ?? []).map((r) => r.id),
    ...(commands ?? []).map((c) => c.id),
  ]);

  const entries = data?.pages.flatMap((p) => p.entries) ?? [];

  // --- Loading state ---
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-primary/80">Activity</p>
          <h1 className="font-display text-3xl">Audit log</h1>
        </div>
        <motion.div
          variants={fadeUp}
          initial={reduce ? false : 'hidden'}
          animate="show"
          className="glass p-2 sm:p-4"
        >
          <Skeleton className="h-96 w-full" />
        </motion.div>
      </div>
    );
  }

  // --- Empty state ---
  if (entries.length === 0) {
    return (
      <div className="space-y-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-primary/80">Activity</p>
          <h1 className="font-display text-3xl">Audit log</h1>
        </div>
        <motion.div
          variants={fadeUp}
          initial={reduce ? false : 'hidden'}
          animate="show"
          className="text-center py-16"
        >
          <h2 className="font-display text-3xl">No audit entries yet</h2>
          <p className="text-muted-foreground mt-4">
            Officer actions will appear here as they happen.
          </p>
        </motion.div>
      </div>
    );
  }

  // --- Table ---
  return (
    <TooltipProvider>
      <div className="space-y-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-primary/80">Activity</p>
          <h1 className="font-display text-3xl">Audit log</h1>
        </div>
        <motion.div
          variants={fadeUp}
          initial={reduce ? false : 'hidden'}
          animate="show"
          className="glass p-2 sm:p-4"
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Officer</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Target</TableHead>
                <TableHead aria-label="Expand row" />
              </TableRow>
            </TableHeader>
            <TableBody>
              <AnimatePresence mode="popLayout" initial={false}>
                {entries.map((entry) => (
                  <AuditLogRow
                    key={entry.id}
                    entry={entry}
                    knownTargetIds={knownTargetIds}
                  />
                ))}
              </AnimatePresence>
            </TableBody>
          </Table>

          {hasNextPage && (
            <div className="flex justify-center pb-4 pt-2">
              <Button
                variant="outline"
                onClick={() => void fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? 'Loading…' : 'Load 50 more'}
              </Button>
            </div>
          )}
        </motion.div>
      </div>
    </TooltipProvider>
  );
}
